import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import storiesApi from '../api/storiesApi';
import { Story, StoryGroup, LoadingStatus } from '../../types';

interface StoriesState {
  feed: StoryGroup[];
  savedFeed: StoryGroup[] | null;
  mine: Story[];
  activeGroupIndex: number | null;
  activeStoryIndex: number;
  viewerOpen: boolean;
  creatorOpen: boolean;
  status: LoadingStatus;
  mineStatus: LoadingStatus;
  error: string | null;
}

const initialState: StoriesState = {
  feed: [],
  savedFeed: null,
  mine: [],
  activeGroupIndex: null,
  activeStoryIndex: 0,
  viewerOpen: false,
  creatorOpen: false,
  status: 'idle',
  mineStatus: 'idle',
  error: null,
};

export const fetchStoryFeed = createAsyncThunk('stories/fetchFeed', async (_, { rejectWithValue }) => {
  try {
    const res = await storiesApi.getFeed();
    return res.data as StoryGroup[];
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } };
    return rejectWithValue(e.response?.data?.message || 'Failed to load stories');
  }
});

export const fetchMyStories = createAsyncThunk('stories/fetchMine', async (_, { rejectWithValue }) => {
  try {
    const res = await storiesApi.getMine();
    return res.data as Story[];
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } };
    return rejectWithValue(e.response?.data?.message || 'Failed to load your stories');
  }
});

export const createStory = createAsyncThunk(
  'stories/create',
  async ({ userId, formData }: { userId: string; formData: FormData }, { rejectWithValue }) => {
    try {
      const res = await storiesApi.create(userId, formData);
      return res.data as Story;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string }; status?: number } };
      return rejectWithValue(e.response?.data?.message || 'Failed to post story');
    }
  },
);

export const deleteStory = createAsyncThunk(
  'stories/delete',
  async ({ userId, storyId }: { userId: string; storyId: string }, { rejectWithValue }) => {
    try {
      await storiesApi.delete(userId, storyId);
      return storyId;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to delete story');
    }
  },
);

export const markStoryViewed = createAsyncThunk(
  'stories/markViewed',
  async (storyId: string) => {
    await storiesApi.markViewed(storyId).catch(() => {});
    return storyId;
  },
);

const storiesSlice = createSlice({
  name: 'stories',
  initialState,
  reducers: {
    openViewer(state, action: PayloadAction<{ groupIndex: number; storyIndex?: number }>) {
      state.activeGroupIndex = action.payload.groupIndex;
      state.activeStoryIndex = action.payload.storyIndex ?? 0;
      state.viewerOpen = true;
    },
    closeViewer(state) {
      state.viewerOpen = false;
      state.activeGroupIndex = null;
      state.activeStoryIndex = 0;
      if (state.savedFeed !== null) {
        state.feed = state.savedFeed;
        state.savedFeed = null;
      }
    },
    setActiveStoryIndex(state, action: PayloadAction<number>) {
      state.activeStoryIndex = action.payload;
    },
    nextStory(state) {
      if (state.activeGroupIndex === null) return;
      const group = state.feed[state.activeGroupIndex];
      if (!group) return;
      if (state.activeStoryIndex < group.stories.length - 1) {
        state.activeStoryIndex += 1;
      } else if (state.activeGroupIndex < state.feed.length - 1) {
        state.activeGroupIndex += 1;
        state.activeStoryIndex = 0;
      } else {
        state.viewerOpen = false;
        state.activeGroupIndex = null;
        state.activeStoryIndex = 0;
      }
    },
    prevStory(state) {
      if (state.activeGroupIndex === null) return;
      if (state.activeStoryIndex > 0) {
        state.activeStoryIndex -= 1;
      } else if (state.activeGroupIndex > 0) {
        state.activeGroupIndex -= 1;
        const prevGroup = state.feed[state.activeGroupIndex];
        state.activeStoryIndex = prevGroup ? prevGroup.stories.length - 1 : 0;
      }
    },
    openCreator(state) {
      state.creatorOpen = true;
    },
    closeCreator(state) {
      state.creatorOpen = false;
    },
    // Temporarily inject a single group (e.g. from ProfileView) — saves current feed so it can be restored on close
    injectAdHocFeed(state, action: PayloadAction<StoryGroup>) {
      state.savedFeed = state.feed;
      state.feed = [action.payload];
      state.activeGroupIndex = 0;
      state.activeStoryIndex = 0;
      state.viewerOpen = true;
    },
    markFeedStoryViewed(state, action: PayloadAction<string>) {
      for (const group of state.feed) {
        for (const story of group.stories) {
          if (story._id === action.payload) {
            story.viewedByMe = true;
          }
        }
        group.hasUnseen = group.stories.some((s) => !s.viewedByMe);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStoryFeed.pending, (state) => { state.status = 'loading'; state.error = null; })
      .addCase(fetchStoryFeed.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.feed = payload;
      })
      .addCase(fetchStoryFeed.rejected, (state, { payload }) => {
        state.status = 'failed';
        state.error = payload as string;
      })
      .addCase(fetchMyStories.pending, (state) => { state.mineStatus = 'loading'; })
      .addCase(fetchMyStories.fulfilled, (state, { payload }) => {
        state.mineStatus = 'succeeded';
        state.mine = payload;
      })
      .addCase(fetchMyStories.rejected, (state) => { state.mineStatus = 'failed'; })
      .addCase(createStory.fulfilled, (state, { payload }) => {
        state.mine = [payload, ...state.mine];
      })
      .addCase(deleteStory.fulfilled, (state, { payload }) => {
        state.mine = state.mine.filter((s) => s._id !== payload);
      });
  },
});

export const {
  openViewer,
  closeViewer,
  setActiveStoryIndex,
  nextStory,
  prevStory,
  openCreator,
  closeCreator,
  markFeedStoryViewed,
  injectAdHocFeed,
} = storiesSlice.actions;

export default storiesSlice.reducer;

export const selectStoryFeed = (state: { stories: StoriesState }) => state.stories.feed;
export const selectMyStories = (state: { stories: StoriesState }) => state.stories.mine;
export const selectViewerOpen = (state: { stories: StoriesState }) => state.stories.viewerOpen;
export const selectCreatorOpen = (state: { stories: StoriesState }) => state.stories.creatorOpen;
export const selectActiveGroupIndex = (state: { stories: StoriesState }) => state.stories.activeGroupIndex;
export const selectActiveStoryIndex = (state: { stories: StoriesState }) => state.stories.activeStoryIndex;
export const selectStoriesStatus = (state: { stories: StoriesState }) => state.stories.status;
export const selectMineStatus = (state: { stories: StoriesState }) => state.stories.mineStatus;
